import { useQuery } from '../models/reactUtils'
import { observer } from 'mobx-react'
import { PropTypes as MobxPropTypes } from 'mobx-react'
import React from 'react'

import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import InfoIcon from '@mui/icons-material/Info'
import LanguageIcon from '@mui/icons-material/Language'
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded'
import LocationIcon from '@mui/icons-material/LocationOn'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import Avatar from '@mui/material/Avatar'
import Fab from '@mui/material/Fab'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import { AppointmentButton } from './AppointmentButton'
import PartnerTierLabel from './PartnerTierLabel'
import SpecialtyIcon from './SpecialtyIcon'

/**
 * @description A Provider result list item component rendered in Results Panel
 * @param {Object} provider An instance of ProviderModel
 * @param {function} toggleDrawer - The callback function which toggles drawer open state
 */
const ProviderListItem = observer(({ provider, toggleDrawer }) => {
  const { store } = useQuery()
  return (
    <ListItem
      divider
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: 3,
        py: 2,
      }}
    >
      <Accordion
        expanded={provider.activeAccordion}
        sx={{
          py: 1,
          px: 2,
          flex: 1,
          boxShadow: 'none',
          bgcolor: 'inherit',
          width: '100%',
        }}
      >
        <Grid container direction="row" flexWrap="nowrap">
          <ListItemAvatar>
            <Avatar
              src={provider.photoUrl || undefined}
              sx={{
                mr: 2,
              }}
            />
          </ListItemAvatar>
          <Grid container direction="column">
            <Grid container alignItems="center">
              <Grid item>
                <Typography variant="h5" color="secondary">
                  {provider.name}
                </Typography>
              </Grid>
              <Grid item>
                {provider.bio && (
                  <IconButton
                    aria-label="expand"
                    color="secondary"
                    onClick={() => {
                      provider.toggleAccordion()
                    }}
                    sx={{ p: 0.75, whiteSpace: 'nowrap' }}
                  >
                    <InfoIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
            {provider.specialtyList && (
              <Grid container alignItems="center">
                <Grid item sx={{ mr: 1 }}>
                  <SpecialtyIcon />
                </Grid>
                <Grid item>
                  <Typography
                    variant="subtitle2"
                    component="p"
                    color="secondary"
                  >
                    {provider.specialtyList}
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid container alignItems="center">
              <Grid item sx={{ mr: 1 }}>
                <LanguageIcon color="secondary" />
              </Grid>
              <Grid item>
                <Typography variant="caption" component="p">
                  {provider.formattedLanguagesSpoken}
                </Typography>
              </Grid>
            </Grid>
            {provider.partnerTier ? (
              <PartnerTierLabel partnerTier={provider.partnerTier} />
            ) : null}
          </Grid>
        </Grid>
        <AccordionDetails>
          <Typography variant="body2">{provider.bio}</Typography>
        </AccordionDetails>
      </Accordion>

      <Grid container direction="column">
        {provider &&
          provider.locations &&
          provider.locations.map(location => {
            return (
              <Grid
                container
                key={location.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  py: 1,
                }}
              >
                <Grid
                  item
                  container
                  justifyContent="space-between"
                  flexWrap="nowrap"
                >
                  <Grid
                    item
                    container
                    direction="column"
                    width="75%"
                    sx={{ mt: 1, ml: 2 }}
                  >
                    <Grid container>
                      <Grid item sx={{ mr: 1 }}>
                        <BusinessRoundedIcon color="secondary" />
                      </Grid>
                      <Grid item>
                        <Link
                          color="inherit"
                          underline="always"
                          onClick={() => {
                            store.locationStore.setFocusLocation(location)

                            toggleDrawer(false)
                          }}
                        >
                          <Typography display="block" variant="caption">
                            {location.street}
                          </Typography>
                          <Typography
                            display="block"
                            variant="caption"
                            gutterBottom
                          >
                            {location.city} {location.state} {location.zip}
                          </Typography>
                        </Link>
                      </Grid>
                    </Grid>
                    <Grid container alignItems="center">
                      <Grid item sx={{ mr: 1 }}>
                        <LocalPhoneRoundedIcon color="secondary" />
                      </Grid>
                      <Grid item>
                        <Link
                          color="inherit"
                          underline="always"
                          href={`tel:${location.formattedPhoneNumber}`}
                        >
                          <Typography display="block" variant="caption">
                            {location.formattedPhoneNumber}
                          </Typography>
                        </Link>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    container
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: '25%',
                      textAlign: 'center',
                    }}
                  >
                    <Fab
                      size="small"
                      aria-label="Show on Map"
                      sx={{
                        width: 36,
                        height: 36,
                        backgroundColor: 'transparent',
                        color: 'primary.main',
                        border: '1px solid',
                        boxShadow: 'none',
                      }}
                      onClick={() => {
                        store.locationStore.setFocusLocation(location)

                        toggleDrawer(false)
                      }}
                    >
                      <LocationIcon />
                    </Fab>
                    <Typography variant="caption">Show on map</Typography>
                  </Grid>
                </Grid>
                <Grid item container>
                  {location.practice.isClaimed && (
                    <AppointmentButton
                      location={location}
                      provider={provider}
                    />
                  )}
                </Grid>
              </Grid>
            )
          })}
      </Grid>
    </ListItem>
  )
})

ProviderListItem.displayName = 'ProviderListItem'
ProviderListItem.propTypes = {
  provider: MobxPropTypes.observableObject,
  toggleDrawer: PropTypes.func,
}

export default ProviderListItem
